import React, { useEffect, useState } from "react";
import Navigation from "./navigation";
import Footer from "./footer";
import LatestEventsSection from "./LatestEventsSection";
import redirectToDonation from "./donationRedirect";
import API_URL from "../apiUrl";

const RenOnIssue = () => {
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchIssues = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await fetch(`${API_URL}/api/issues/`);
        if (!res.ok) throw new Error("Failed to fetch issues");
        const data = await res.json();
        setIssues(Array.isArray(data) ? data : []);
      } catch (e) {
        setError("Failed to load issues");
      } finally {
        setLoading(false);
      }
    };
    fetchIssues();
  }, []);

  const getImageSrc = (img) => {
    if (!img) return null;
    if (typeof img === 'string' && (img.startsWith('http://') || img.startsWith('https://') || img.startsWith('//'))) return img;
    return `${API_URL}${img}`;
  };

  return (
    <>
        <div className="fixed top-0 left-0 right-0 z-50">
        <Navigation />
      </div>

      <div className="hero-section2 px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="text-white text-center text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold py-8">
          Ren On Issues
        </div>
      </div>

      <div className="section-margin-half"></div>

      <div className="max-width px-4 sm:px-6 md:px-8 lg:px-12">
        <p className="text-center text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700">
          Public service is a trust, and I will be accountable to this community every day I serve. I pledge to hold regular town halls, coffee hours, and community service events so constituents can always make their voices heard. I will support ethics reform to increase transparency in Lansing, reduce special interest influence in policymaking, and ensure that campaign finance is fair and open.
        </p>
      </div>
      
      <div className="section-margin"></div>

      <div className="max-width px-4 sm:px-6 md:px-8 lg:px-12">
        {/* Render DB-fetched issues */}
        {loading && (
          <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
            <div className="w-full text-center">
              <p className="text-base sm:text-lg md:text-xl">Loading issues...</p>
            </div>
          </div>
        )}
        
        {error && (
          <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
            <div className="w-full text-center">
              <p className="text-base sm:text-lg md:text-xl text-red-500">{error}</p>
            </div>
          </div>
        )}
        
        {issues && issues.length > 0 && issues.map(issue => {
          const imgSrc = getImageSrc(issue.image);
          return (
            <div key={issue.id} className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
              <div className="w-full lg:w-1/2">
                <img 
                  src={imgSrc || "https://upload.wikimedia.org/wikipedia/commons/1/14/No_Image_Available.jpg"} 
                  alt={issue.title || "Issue image"} 
                  className="w-full h-auto rounded-lg shadow-lg object-cover"
                />
              </div>
              <div className="w-full lg:w-1/2">
                {issue.title && (
                  <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
                    {issue.title}
                  </h2>
                )}
                {issue.description && (
                  <p className="text-base sm:text-lg md:text-xl text-gray-700">
                    {issue.description}
                  </p>
                )}
              </div>
            </div>
          );
        })}

        {/* Static Issues Sections - Images URLs remain unchanged */}
        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2">
            <img 
              src="https://media.istockphoto.com/id/1346853640/photo/saving-money-concept-man-hand-putting-row-and-coin-write-finance-saving-money-concept-man.jpg?s=612x612&w=0&k=20&c=1I48V9GUU0liAJ-dMA4SW-h5LoejkBTlEZJ-0b_vmXE=" 
              alt="Jobs and Economy" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
              Jobs, Wages, and a Fair Economy
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              This community deserves a government and legislators that work for them. I support raising the minimum wage to a living wage and indexing it to inflation, so families are never left behind. I'll fight to protect the right to organize and collectively bargain, and I support strong labor laws that ensure safe working conditions. I will also champion policies that attract clean energy and advanced manufacturing jobs to our region—jobs that pay well and can't be outsourced.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2 order-2 lg:order-1">
            <img 
              src="https://media.istockphoto.com/id/1358014313/photo/group-of-elementary-students-having-computer-class-with-their-teacher-in-the-classroom.jpg?s=612x612&w=0&k=20&c=3xsykmHXFa9ejL_sP2Xxiow7zdtmKvg15UxXFfgR98Q=" 
              alt="Education" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2 order-1 lg:order-2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
              Education and Opportunity for Every Child
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Our public schools are the backbone of our communities, and they deserve our full support. I will fight to fully fund public education and oppose efforts to privatize or defund our school systems. We must ensure our teachers are respected, well-paid, and supported with the resources they need to succeed. I support universal access to pre-K, expansion of after-school and enrichment programs, and tuition-free options at community colleges and skilled trades training centers to give every student a pathway to success.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2">
            <img 
              src="https://wallpaper.dog/large/20578700.jpg" 
              alt="Healthcare" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
              Health Care You Can Count On
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Health care is a human right. I will work to protect and expand the Healthy Michigan Plan (our Medicaid expansion) to ensure more working families can get the coverage they need. I support capping insulin and prescription drug costs and holding pharmaceutical companies accountable for price gouging. We also need to address the growing mental health crisis by increasing state funding for community-based mental health services, expanding school-based counseling, and reducing the stigma around getting help.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2 order-2 lg:order-1">
            <img 
              src="https://w0.peakpx.com/wallpaper/647/586/HD-wallpaper-design-houses-house-layout-drawing-construction-of-houses.jpg" 
              alt="Affordable Housing" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2 order-1 lg:order-2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
              Affordable Housing & Strong Communities
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Everyone deserves a safe and affordable place to call home. I will fight to expand funding for affordable and workforce housing so that young families starting out and seniors on fixed incomes can continue living in the communities they helped build. I support incentives for mixed-use development and transit-accessible housing that reflect the needs of modern suburban residents. I will push for increased state grants to help local governments invest in infrastructure and revitalize our neighborhoods with green, walkable, and sustainable design.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2">
            <img 
              src="https://plus.unsplash.com/premium_photo-1661602106936-1afc109215a6?fm=jpg&q=60&w=3000&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxzZWFyY2h8MTd8fHdvbWVuJTIwcmlnaHRzfGVufDB8fDB8fHww" 
              alt="Public Safety" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
              Public Safety and Justice Reform
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              As a small-town prosecutor, public safety is near and dear to my heart. True public safety means building trust and ensuring equal protection for all. I support increasing funding for mental health crisis response teams and non-police alternatives that can de-escalate emergencies. Our officers deserve modern training, clear accountability, and the resources they need to serve our communities safely. I will also support reforms to reduce recidivism, expand access to rehabilitation and reentry programs, and end the use of cash bail for nonviolent offenses.
            </p>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2 order-2 lg:order-1">
            <img 
              src="https://raclinmurphymuseum.nd.edu/assets/599266/wtww_rachel_mills_rachel_mills.jpg" 
              alt="Civil Rights" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2 order-1 lg:order-2">
            <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-4 text-gray-800" style={{ color: "var(--primary)" }}>
              Civil Rights
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Civil rights are not negotiable; they are the foundation of democracy. I will fight to protect and expand the rights of every Michigander, regardless of race, religion, gender, sexual orientation, disability, or immigration status. That means defending voting rights, combating systemic discrimination, and ensuring equal access to housing, education, health care, and employment. I will stand firmly against book bans, censorship, and efforts to erase the identities and histories of marginalized communities. In Senate District 9, we celebrate our diversity—and I will work every day to make sure our laws do the same.
            </p>
          </div>
        </div>

        {/* Statement Section */}
        <div className="mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-6 text-gray-800" style={{ color: "var(--primary)" }}>
            Statement from Ren Nushaj
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-6">
            I believe deeply in the dignity of all people and the importance of supporting efforts that lead to peace, safety, and compassion—especially in times of great human suffering. Food, water, and safety are not political talking points; they are fundamental human rights.
          </p>
        </div>

        {/* Gaza/Israel Section */}
        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2">
            <img 
              src="https://www.aljazeera.com/wp-content/uploads/2014/07/201472382635591734_20.jpeg?resize=570%2C380&quality=80" 
              alt="Humanitarian Aid" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2">
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Recent events in Gaza and Israel have left so many in despair and struggling for basic necessities. In that spirit, I respectfully call on all leaders to support:
              <br /><br />
              The urgent and consistent delivery of humanitarian aid to civilians in need;
              <br /><br />
              The immediate and safe return of all hostages;
              <br /><br />
              A path toward an immediate and sustained ceasefire to prevent further loss of life and lay the groundwork for long-term peace.
            </p>
          </div>
        </div>

        {/* Closing Statement */}
        <div className="mb-8 sm:mb-12 md:mb-16">
          <p className="text-base sm:text-lg md:text-xl text-gray-700 text-center lg:text-left">
            Like so many in our community, I am heartbroken and outraged by the suffering and devastation we have witnessed. In moments like these, our shared values—compassion, humanity, and justice—must guide both our response and our call to action.
            <br /><br />
            I remain hopeful that through principled leadership, international cooperation, and a collective commitment to peace, we can build a better future for all who are suffering.
          </p>
        </div>

        {/* Climate Change Section */}
        <div className="mb-8 sm:mb-12 md:mb-16">
          <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold mb-6 text-gray-800" style={{ color: "var(--primary)" }}>
            Climate Change
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 mb-6">
            Climate change is not a distant threat—it is the defining challenge of our generation. Every wildfire, every flood, every extreme heat wave is a reminder that we cannot continue to rely on outdated systems that harm both our environment and our communities.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row items-center gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-8 sm:mb-12 md:mb-16">
          <div className="w-full lg:w-1/2 order-2 lg:order-1">
            <img 
              src="https://c4.wallpaperflare.com/wallpaper/504/431/580/desert-storm-wheather-weather-changes-wallpaper-preview.jpg" 
              alt="Climate Change" 
              className="w-full h-auto rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="w-full lg:w-1/2 order-1 lg:order-2">
            <p className="text-base sm:text-lg md:text-xl text-gray-700">
              Progressive climate policies are not just about reducing carbon emissions; they are about protecting public health, creating sustainable jobs, and ensuring that our children inherit a livable world.
              <br /><br />
              Transitioning to clean energy and investing in renewable technologies will not only reduce our dependence on fossil fuels but also spur innovation and economic growth. A just transition ensures that workers and communities historically tied to carbon-heavy industries are supported with training, investment, and opportunities in the green economy.
            </p>
          </div>
        </div>

        <div className="mb-8 sm:mb-12 md:mb-16">
          <p className="text-base sm:text-lg md:text-xl text-gray-700">
            It is my personal belief that environmental justice and social justice are inseparable. Low-income families and communities of color are too often the first to bear the brunt of pollution and climate disasters, and they must be at the center of our solutions.
            <br /><br />
            We must act with urgency and boldness. The choice before us is clear: either cling to the status quo and face worsening climate disasters, or lead with vision, courage, and compassion toward a cleaner, safer, and more equitable future.
          </p>
        </div>
      </div>

      <div className="section-margin"></div>

      {/* Events Section - Responsive */}
      <div className="px-4 sm:px-6 md:px-8 lg:px-12">
        <div className="hidden lg:block">
          <LatestEventsSection count={3} />
        </div>
        <div className="lg:hidden">
          <LatestEventsSection count={2} />
        </div>
      </div>

      <div className="section-margin-half"></div>
      <div className="section-margin"></div>

      {/* Donate Section */}
     <div className="max-width overflow-hidden">
        <div className="donate-ornage-section mb-vertical space-between justify-between">
          <div className="text-center text-white sm:text-left text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-bold">
            Donate to Our Campaign.
          </div>
          <div className="horizontal ">
            <button
              className="jose donate-btn-white"
              style={{ width: 150, padding: 15 }}
              onClick={redirectToDonation}
            >
              Donate Now
            </button>
          </div>
        </div>
      </div>

      <div className="section-margin"></div>
      <Footer />
    </>
  );
}

export default RenOnIssue;